import React from "react";
import { Box, Typography } from "@mui/material";

type ShowAsSoldOutProps = {
    isSoldOut: boolean;
    /** Only invoked when NOT sold out */
    onClick?: (e: React.MouseEvent) => void;
    sx?: any; // wrapper sx
    childSx?: any; // sx applied to the measured child container (position: relative)
    children: React.ReactNode;
};

/**
 * Renders children and, when isSoldOut, places an overlay INSIDE the child container
 * so the overlay exactly matches the child's width/height.
 */
export function ShowAsSoldOut({
    isSoldOut,
    onClick,
    sx,
    childSx,
    children,
}: ShowAsSoldOutProps) {
    const handleClick = (e: React.MouseEvent) => {
        if (isSoldOut) {
            e.stopPropagation();
            e.preventDefault();
            return;
        }
        onClick?.(e);
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                position: "relative",
                display: "block", // keep layout as block by default
                width: "100%",
                maxWidth: { xs: 360, sm: 600 },
                cursor: "pointer",
                ...sx,
            }}
        >
            {/* The child container is position:relative so the overlay can be absolutely inset to it */}
            <Box
                sx={{
                    position: "relative",
                    // default display can be overridden by caller via childSx
                    display: "block",
                    ...childSx,
                }}
            >
                {/* Visual dimming */}
                <Box
                    sx={{
                        filter: isSoldOut ? "grayscale(100%)" : "none",
                        opacity: isSoldOut ? 0.65 : 1,
                        transition: "filter 200ms, opacity 200ms",
                    }}
                >
                    {children}
                </Box>

                {/* Overlay lives inside the child container so it matches its exact size */}
                {isSoldOut && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0, // top:0; left:0; right:0; bottom:0
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "auto", // intercept clicks
                            backgroundColor: "rgba(255,255,255,0.45)",
                            borderRadius: 1,
                            zIndex: 5,
                        }}
                    >
                        <Typography variant="h6" color="text.secondary">
                            Sold out
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
